import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBoolean, useCopyToClipboard } from 'usehooks-ts';

import { Button, Image, Paragraph } from '@app/shared/ui/atoms/Common';
import { StoneDetails } from '@app/shared/ui/molecules/StoneAbilities';
import { CarouselDots } from '@app/shared/ui/molecules/Common';
import { StoneCarousel } from '@app/shared/ui/organisms/Cognition';
import { Modal } from '@app/shared/ui/organisms/Common';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import { useBalance } from '@app/shared/hooks/useBalance';
import { useBuyStoneMutation } from '@app/store/slices/stones/stonesApi';
import { telegramVibrate } from '@app/shared/utils/common';
import notification from '@app/shared/utils/notification';
import { PriceType } from '@app/shared/types/common';
import { FrontendEnergyStone } from '@app/shared/types/pages/stones';
import { Page } from '@app/routes';
import {
  useGetCurrentUserProfileQuery,
  useGetCurrentUserQuery,
  useSendGiftMutation,
} from '@app/store/slices/users/usersApi';
import { giftImage } from '@/app/assets';
import { CountSelect } from '@app/shared/ui/molecules/ProfitCalculator';
import { TG_BOT_URL } from '@/app/shared/constants/api';
import { SendGiftBody } from '@/app/store/slices/users/usersTypes';

interface StoneAbilitiesContentProps {
  energyStones: FrontendEnergyStone[];
}

export const StoneAbilitiesContent = ({ energyStones }: StoneAbilitiesContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stoneIdFromUrl = searchParams.get('stone');
  const { data: userProfile } = useGetCurrentUserProfileQuery();
  const [giftsCount, setGiftsCount] = useState<string | undefined>('1');
  const [isGiftStoneModalOpen, setIsGiftStoneModalOpen] = useState(false);
  const [giftStoneNodeLevel, setGiftStoneNodeLevel] = useState<number | undefined>(0);
  const [sendGift] = useSendGiftMutation();
  const { data: user } = useGetCurrentUserQuery();
  const { isBalanceEnough, fetchBalance } = useBalance();
  const [, copyToClipboard] = useCopyToClipboard();

  const { handleFetchError } = useApiErrorHandler();

  const [buyStone, { isLoading: isBuyingStone }] = useBuyStoneMutation();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const {
    value: isStoneModalOpen,
    setTrue: openStoneModal,
    setFalse: closeStoneModal,
  } = useBoolean(false);

  useEffect(() => {
    if (stoneIdFromUrl) {
      const stoneIndex = energyStones.findIndex((stone) => stone.id === Number(stoneIdFromUrl));
      if (stoneIndex >= 0) {
        setSelectedIndex(stoneIndex + 1);
        openStoneModal();
      }
    }
  }, []);

  const selectedStone = useMemo(() => {
    const stoneArrayIndex = selectedIndex - 1;
    return stoneArrayIndex >= 0 && stoneArrayIndex < energyStones.length
      ? energyStones[stoneArrayIndex]
      : null;
  }, [energyStones, selectedIndex]);

  const handleBuyStone = async () => {
    if (!selectedStone) {
      return;
    }
    telegramVibrate();

    if (!isBalanceEnough(PriceType.COOKIE, selectedStone.price)) {
      notification.error(t('errors.balance'));
      return;
    }

    try {
      const response = await buyStone({
        stoneId: selectedStone.id,
        stonePrice: selectedStone.price,
      }).unwrap();

      if (response.ok) {
        handleCloseModal();
        notification.success(t('cognition.buy_success', { stoneTitle: selectedStone.name }));

        const hasBirthday = Boolean(userProfile?.birthday);

        if (hasBirthday) {
          const firstStoneAbilityIndex = selectedStone.abilities[0].matrixIndex;
          void navigate(
            `/matrix/channels/${String(selectedStone.id)}/${String(firstStoneAbilityIndex)}`
          );
        } else {
          void navigate(`${Page.USER_DATA_FORM}?stoneId=${String(selectedStone.id)}`);
        }
      }
    } catch (err) {
      handleFetchError(err);
    }
  };

  const handleAbilityClick = useCallback(
    (stoneId: number, matrixIndex: number, isPurchased: boolean) => {
      telegramVibrate();

      if (isPurchased) {
        void navigate(`/matrix/channels/${String(stoneId)}/${String(matrixIndex)}`);
      } else {
        const idx = energyStones.findIndex((stone) => stone.id === stoneId);
        if (idx >= 0) {
          setSelectedIndex(idx + 1);
          openStoneModal();
        }
      }
    },
    [energyStones, navigate]
  );

  const handleCloseModal = useCallback(() => {
    telegramVibrate();
    closeStoneModal();
    setSearchParams({});
  }, []);

  const handleSendGift = async () => {
    if (!user) {
      throw new Error('No user');
    }
    telegramVibrate();
    if (!giftsCount) {
      return;
    }
    if (!selectedStone) {
      return;
    }
    if (giftStoneNodeLevel === undefined) {
      return;
    }

    if (!isBalanceEnough(PriceType.TON, selectedStone.price * Number(giftsCount))) {
      notification.error(t('errors.balance'));
      return;
    }

    const giftBody: SendGiftBody = {
      to: Number(giftsCount),
      action: 'STONE',
      nodeLevel: giftStoneNodeLevel,
      balance: 'ton',
      maxWaiting: 1440,
    };

    try {
      const res = await sendGift(giftBody).unwrap();
      const token = res.data?.giftToken;
      if (!token) {
        throw new Error('No gift token');
      }
      const link = `${TG_BOT_URL}?start=${String(user.id)}-gift-${token}`;
      await copyToClipboard(link);
      notification.success(t('home.gift_link_copied'));
      await fetchBalance();
    } catch (err: unknown) {
      handleFetchError(err);
    }
  };

  return (
    <>
      <ul className="space-y-4 overflow-y-auto pt-4 scrollbar-hide">
        {energyStones.map((stone) => (
          <li key={stone.id}>
            <StoneDetails stone={stone} onAbilityClick={handleAbilityClick} />
          </li>
        ))}
      </ul>

      <Modal
        isOpen={isStoneModalOpen}
        onOpenChange={handleCloseModal}
        hiddenTitle={selectedStone?.name}
      >
        <div className="-mt-[120px] space-y-5 overflow-hidden">
          <StoneCarousel
            stones={energyStones}
            onStoneChange={setSelectedIndex}
            onBuyBtnClick={handleBuyStone}
            isBuyDisabled={isBuyingStone}
            selectedIndex={selectedIndex}
            onGiftClick={(nodeLevel: number) => {
              setGiftStoneNodeLevel(nodeLevel);
              setIsGiftStoneModalOpen(true);
            }}
          />
          <CarouselDots carouselLength={energyStones.length + 1} currentIndex={selectedIndex} />
        </div>
      </Modal>

      <Modal
        isOpen={isGiftStoneModalOpen}
        onOpenChange={setIsGiftStoneModalOpen}
        featuredImg={<Image src={giftImage} />}
        hiddenTitle="Send gift"
        contentClassName="shadow-blue"
      >
        <Paragraph className="whitespace-pre-line">
          {t('gift_modal.text_stone', {
            stone: selectedStone?.name,
          })}
        </Paragraph>

        <CountSelect
          value={giftsCount}
          onChange={setGiftsCount}
          length={10}
          itemKey="gift_modal.gift"
        />
        <Paragraph size="xs">{t('gift_modal.link_expires')}</Paragraph>

        <div className="flex gap-2">
          <Button onClick={handleSendGift}>{t('common.generate_link')}</Button>
        </div>
      </Modal>
    </>
  );
};
